import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Quote } from 'lucide-react';
import '../styles/TestimonialsCarousel.css';

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const testimonials = [
    {
      id: 'QYttZRPb3GA',
      title: 'Outlet de Viajes',
      company: 'Outlet de Viajes',
    },
    {
      id: 'psIWbIcsf_8',
      title: 'Zatecno',
      company: 'Zatecno',
    },
    {
      id: 'wYIThx6_70g',
      title: 'Working Depot',
      company: 'Working Depot',
    },
    {
      id: 'uNPN5cKgqJI',
      title: 'IPEA',
      company: 'IPEA',
    },
    {
      id: 'JfCZQ3VWcVM',
      title: 'Primer Luna',
      company: 'Primer Luna',
    },
    {
      id: '5cpCc4DKs9o',
      title: 'DiMagic',
      company: 'DiMagic',
    },
    {
      id: 'nCW7Eb4u_yg',
      title: 'Stockar',
      company: 'Stockar',
    },
    {
      id: 'NrqH3TJkjQ0',
      title: 'Tecno Electro Pampa',
      company: 'Tecno Electro Pampa',
    },
    {
      id: 'oGDWumkV_ek',
      title: 'iHOUSE',
      company: 'iHOUSE',
    },
    {
      id: '6_Ln_YS9em0',
      title: 'Experiencia Keen',
      company: 'Experiencia Keen',
    },
    {
      id: 'UsQBJI_xOq4',
      title: 'Camping El Dorado',
      company: 'Camping El Dorado',
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getVisibleCards = useCallback(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  }, [isMobile, isTablet]);

  const visibleCards = getVisibleCards();
  const totalSlides = testimonials.length;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Calcular los índices de las tarjetas visibles con wrap-around
  const getVisibleTestimonials = () => {
    const result = [];
    for (let i = 0; i < visibleCards; i++) {
      const index = (currentIndex + i) % totalSlides;
      result.push({ ...testimonials[index], displayIndex: i });
    }
    return result;
  };

  const visibleTestimonials = getVisibleTestimonials();

  return (
    <div className="testimonials-carousel-wrapper">
      <motion.div 
        className="testimonials-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="testimonials-badge">
          <Quote size={18} />
          <span>Testimonios Reales</span>
        </div>
        <h3>Lo Que Dicen Nuestros <span className="text-gradient">Clientes</span></h3>
        <p className="testimonials-subtitle">
          Descubre cómo empresas como la tuya han transformado su atención al cliente con Runamatic
        </p>
      </motion.div>

      <div className="carousel-container">
        <button 
          className="carousel-nav-button prev" 
          onClick={prevSlide}
          aria-label="Testimonio anterior"
        >
          <ChevronLeft size={24} />
        </button>

        <div className="carousel-track-container">
          <div className="carousel-track">
            {visibleTestimonials.map((testimonial, index) => (
              <motion.div
                key={`${testimonial.id}-${currentIndex}-${index}`}
                className="testimonial-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="video-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${testimonial.id}`}
                    title={`Testimonio ${testimonial.title}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  />
                  <div className="video-overlay">
                    <Play size={48} />
                  </div>
                </div>
                <div className="testimonial-info">
                  <h4>{testimonial.company}</h4>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <button 
          className="carousel-nav-button next" 
          onClick={nextSlide}
          aria-label="Siguiente testimonio"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Indicadores de progreso */}
      <div className="carousel-indicators">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Ir al testimonio ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
